import {
  ControlField,
  FieldMetadata,
} from '../../../../contexts/MetadataContext';
import { PropertyField } from '../../../../system/field';
import {
  HistoricalListingData,
  ListingData,
} from '../../../../system/recommendations';

export const ASSESSOR_SOURCE = 'Assessor';

/* A namespace is the clearest grouping for this family of pure payload types;
   converting to ES module exports would ripple `PayloadTypes.*` across many files. */
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace PayloadTypes {
  export type SetTemplatePayload = {
    templateName: string;
    templateData: {
      [key: string]: {
        title: string;
        flattenFields: any[];
      };
    };
  };

  export type SetRecordsPayload = {
    records: ListingRecord[];
  };

  export type InitPayload = {
    getFieldMetadata: (key: string) => any;
    initialState?: ListingState;
  };

  export type PersistResolvedPayload = Record<string, never>;

  export type AddFieldPayload = {
    field: Field;
    recordValue: RecordValue;
  };

  export type UpdateFieldPayload = {
    field: Field;
    recordValue: RecordValue;
  };

  export type RemoveFieldPayload = {
    field: Field;
    recordValue: RecordValue;
  };

  export type EnableRecordPayload = {
    record: ListingRecord;
  };

  export type MetricsPayload = {
    metrics: any;
  };

  export type HiddenFieldsPayload = {
    type: string;
    fields: PropertyField[];
  };

  export type ResolveFieldPayload = {
    field: Field;
    value: FieldData;
  };
}

export enum RecordTypes {
  LISTING = 'LISTING',
  TAX = 'TAX',
  AI = 'AI', // Added AI as a record type
}

export enum RadioGroupTypes {
  CUSTOM = 'CUSTOM',
  NONE = 'NONE',
}

export enum ListingActionType {
  ADD_FIELD = 'ADD_FIELD',
  ENABLE_RECORD = 'ENABLE_RECORD',
  INIT = 'INIT',
  REMOVE_FIELD = 'REMOVE_FIELD',
  RESOLVE_FIELD = 'RESOLVE_FIELD',
  SET_HIDDEN_FIELDS = 'SET_HIDDEN_FIELDS',
  SET_RECORDS = 'SET_RECORDS',
  SET_TEMPLATE = 'SET_TEMPLATE',
  UPDATE_FIELD = 'UPDATE_FIELD',
  UPDATE_METRICS = 'UPDATE_METRICS',
  UPDATE_TEMPLATE_FIELDS = 'UPDATE_TEMPLATE_FIELDS',
  PERSIST_RESOLVED = 'PERSIST_RESOLVED',
}

export enum ListingMetricType {
  NEEDS_REVIEW = 'NEEDS_REVIEW',
  NO_CONFLICT_FIELDS = 'NO_CONFLICT_FIELDS',
  READY_TO_IMPORT = 'READY_TO_IMPORT',
  REMAINING_REVIEW_FIELDS = 'REMAINING_REVIEW_FIELDS',
  SOLVED_BY_USER = 'SOLVED_BY_USER',
}

export enum ListingStateKey {
  CONFIG = 'Config',
  ENABLED_RECORDS = 'EnabledRecords',
  HIDDEN_FIELDS = 'HiddenFields',
  METRICS = 'Metrics',
  READY_TO_IMPORT_LIST = 'ReadyToImportFieldList',
  RECORDS = 'Records',
  RECORDS_FIELDLIST = 'RecordsFieldList',
  TEMPLATE = 'Template',
  TEMPLATE_FIELDLIST = 'TemplateFieldList',
  PREVIOUS_TEMPLATE_LIST = 'PreviousTemplateList',
  RESOLVED_FIELDS = 'ResolvedFields',
}

export interface ListingConfig {
  persistResolvedData: boolean;
  includeUnselectedListings: boolean;
  getFieldMetadata?: (key: string) => any;
}

export interface ListingRecord extends HistoricalListingData {
  id: string;
  isEnabled?: boolean;
  type: string;
  label: string;
}

export type MetricsType = {
  [key in ListingMetricType]?: number;
};

export interface ListingMetrics extends MetricsType {
  [key: string]: Record<string, any> | any;
}

export interface ListingSection {
  fields: Field[];
  key: string;
  listedFields: Record<string, boolean>;
  relatedRecordId: string;
  title: string;
  totalFields: number;
}

export interface TemplateData {
  current: string;
  [key: string]: any;
}

export interface FieldData {
  label?: string;
  recordId: string;
  recordType?: string;
  value: any;
}

export interface FieldDataPersisted extends FieldData {
  persisted: boolean;
}

export interface ReviewOptionData {
  value: any;
  recordId: string;
  label: string;
}

export interface ReviewDataProps {
  hasConflictData: boolean;
  valuesArrays: any[];
  optionsToShow: FieldData[];
  autoResolved?: boolean; // Added for tracking auto-resolution
}

export interface Field {
  controlField?: ControlField | null;
  currentRecordData: FieldData;
  key: PropertyField;
  sourceField?: {
    key: string;
    value: any;
  };
  metadata?: FieldMetadata | null;
  recordData?: {
    [key: string]: FieldData;
  };
  resolvedData?: FieldData;
  reviewProps?: ReviewDataProps;
  readyToImportValues?: {
    added?: Record<string, FieldData>;
    hasValues: boolean;
  };
}

export interface RecordValue {
  recordId: string;
  value: any;
}

export interface FieldsList {
  [key: string]: Field;
}

export interface PreviousTemplateList {
  [key: string]: boolean;
}

export interface FieldsAndSourceList {
  fields: FieldsList;
  sourceFields: FieldsList;
  sections?: ListingSection[];
}

export interface UnselectedRecord {
  date: string;
  recordId: string;
  label?: string;
  listedSections: FieldsAndSourceList;
  unselectedFieldsData: ListingData;
}

export interface UnselectedRecordList {
  [key: string]: UnselectedRecord;
}

export type ListingAction =
  | {
      type: ListingActionType.INIT;
      payload: PayloadTypes.InitPayload;
    }
  | {
      type: ListingActionType.ADD_FIELD;
      payload: PayloadTypes.AddFieldPayload;
    }
  | {
      type: ListingActionType.REMOVE_FIELD;
      payload: PayloadTypes.RemoveFieldPayload;
    }
  | {
      type: ListingActionType.ENABLE_RECORD;
      payload: PayloadTypes.EnableRecordPayload;
    }
  | {
      type: ListingActionType.SET_RECORDS;
      payload: PayloadTypes.SetRecordsPayload;
    }
  | {
      type: ListingActionType.SET_TEMPLATE;
      payload: PayloadTypes.SetTemplatePayload;
    }
  | {
      type: ListingActionType.UPDATE_METRICS;
      payload: PayloadTypes.MetricsPayload;
    }
  | {
      type: ListingActionType.UPDATE_FIELD;
      payload: PayloadTypes.UpdateFieldPayload;
    }
  | {
      type: ListingActionType.RESOLVE_FIELD;
      payload: PayloadTypes.ResolveFieldPayload;
    }
  | {
      type: ListingActionType.UPDATE_TEMPLATE_FIELDS;
      payload: Record<string, never>;
    }
  | {
      type: ListingActionType.PERSIST_RESOLVED;
      payload: PayloadTypes.PersistResolvedPayload;
    }
  | {
      type: ListingActionType.SET_HIDDEN_FIELDS;
      payload: PayloadTypes.HiddenFieldsPayload;
    };

// An interface for our state
export interface ListingState {
  [ListingStateKey.CONFIG]: ListingConfig;
  [ListingStateKey.ENABLED_RECORDS]: Record<string, ListingRecord>;
  [ListingStateKey.HIDDEN_FIELDS]: {
    fromListing: PropertyField[];
    fromTax: PropertyField[];
  };
  [ListingStateKey.METRICS]: ListingMetrics;
  [ListingStateKey.READY_TO_IMPORT_LIST]: FieldsAndSourceList;
  [ListingStateKey.RECORDS_FIELDLIST]: FieldsAndSourceList;
  [ListingStateKey.RECORDS]: ListingRecord[];
  [ListingStateKey.RESOLVED_FIELDS]: {
    [key: string]: FieldDataPersisted;
  };
  [ListingStateKey.TEMPLATE_FIELDLIST]: FieldsAndSourceList;
  [ListingStateKey.PREVIOUS_TEMPLATE_LIST]: PreviousTemplateList;
  [ListingStateKey.TEMPLATE]: {
    [key: string]: any;
    current: string;
  };
}
